/**
 * WO-D5 — "+ Add service" picker (the shortcut-and-edit centerpiece).
 *
 * Pushed OVER the tabs on the (app) stack from the estimate builder with the estimate id as a
 * search param (?estimateId=…) — opaque UUID, no PII in URL (CLAUDE.md §2). Lists the org's RANKED
 * curated services (GET /api/services/suggested); tapping one POSTs /api/estimates/:id/add-service,
 * which materializes the service into editable, snapshotted material+labor lines on the estimate,
 * then returns to the builder (which re-renders the new lines, fully editable).
 *
 * States: loading / error / empty (no services) / NO-TRADES-SELECTED (→ prompt to pick trades).
 * Every control is a real backend round-trip via TanStack Query. NO mock data, no no-op handlers.
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1). No raw style values.
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listSuggestedServices, getOrgTrades, addServiceToEstimate } from '../../../src/api/services';
import { Screen, Text, Card, Button, ListRow } from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { extractErrorMessage, formatCurrency } from '../../../src/lib/format';
import type { SuggestedServiceDto, OrgTradeDto } from '@beam/api-types';

export default function AddServiceScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { estimateId } = useLocalSearchParams<{ estimateId: string }>();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const orgTradesQuery = useQuery({
    queryKey: ['org-trades'],
    queryFn: getOrgTrades,
    staleTime: 30_000,
  });
  const servicesQuery = useQuery({
    queryKey: ['suggested-services'],
    queryFn: () => listSuggestedServices(20),
    staleTime: 30_000,
    enabled: (orgTradesQuery.data?.length ?? 0) > 0,
  });

  const addMutation = useMutation({
    mutationFn: (serviceTemplateId: string) => addServiceToEstimate(estimateId!, serviceTemplateId),
    onSuccess: (result) => {
      // The response carries the recomputed EstimateDetail — prime the builder's cache so it shows
      // the new lines immediately, then return to it.
      queryClient.setQueryData(['estimates', estimateId], result.estimate);
      void queryClient.invalidateQueries({ queryKey: ['estimates'] });
      router.back();
    },
    onSettled: () => setPendingId(null),
  });

  if (!estimateId) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Text variant="body" color="danger">
            No estimate to add a service to.
          </Text>
        </View>
      </Screen>
    );
  }

  const trades: OrgTradeDto[] = orgTradesQuery.data ?? [];
  const noTrades = orgTradesQuery.isSuccess && trades.length === 0;
  const services: SuggestedServiceDto[] = servicesQuery.data ?? [];

  return (
    <Screen scroll>
      <Text variant="title">Add a service</Text>
      <Text variant="body" color="secondary">
        Pick a service and we'll prefill its materials and labor, fully editable on the estimate.
      </Text>

      {orgTradesQuery.isLoading ? (
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading services"
          />
        </View>
      ) : noTrades ? (
        <Card>
          <Text variant="bodyStrong">No trades selected yet</Text>
          <Text variant="body" color="secondary">
            Pick the trades you work in and we'll suggest the services you estimate most.
          </Text>
          <Button
            title="Pick your trades"
            onPress={() => router.push('/(app)/account/trades')}
            accessibilityLabel="Pick your trades"
          />
        </Card>
      ) : servicesQuery.isLoading ? (
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading services"
          />
        </View>
      ) : servicesQuery.isError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(servicesQuery.error, 'Could not load services.')}
          </Text>
          <Button
            title="Retry"
            variant="secondary"
            size="sm"
            fullWidth={false}
            onPress={() => servicesQuery.refetch()}
          />
        </Card>
      ) : services.length === 0 ? (
        <Card>
          <Text variant="body" color="secondary">
            No suggested services for your trades yet.
          </Text>
        </Card>
      ) : (
        <View style={styles.list}>
          {services.map((s, index) => {
            const busy = addMutation.isPending && pendingId === s.id;
            const anyBusy = addMutation.isPending;
            const hours = s.estimatedHours > 0 ? `${s.estimatedHours} hr` : null;
            const subtitle = [s.tradeName, hours, `~${formatCurrency(s.estimatedTotal)}`]
              .filter(Boolean)
              .join(' · ');
            return (
              <ListRow
                key={s.id}
                testID={`suggested-service-${index}`}
                title={s.name}
                subtitle={subtitle}
                right={
                  busy ? (
                    <ActivityIndicator
                      color={theme.color.action.primary}
                      accessibilityLabel={`Adding ${s.name}`}
                    />
                  ) : (
                    <Text variant="label" color="link">
                      Add
                    </Text>
                  )
                }
                disabled={anyBusy}
                onPress={() => {
                  setPendingId(s.id);
                  addMutation.mutate(s.id);
                }}
                accessibilityLabel={`Add service ${s.name}`}
              />
            );
          })}
        </View>
      )}

      {addMutation.isError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(addMutation.error, 'Could not add that service.')}
          </Text>
        </Card>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: { paddingVertical: theme.spacing['2xl'], alignItems: 'center' },
  list: { gap: theme.spacing.sm },
});
